/**
 * Единицы измерения OOXML.
 *
 * twip (dxa) — 1/20 пункта: поля, отступы, интервалы;
 * полупункт — кегль (w:sz="28" — это 14 пт);
 * EMU — размеры рисунков DrawingML (1 мм = 36 000 EMU).
 */

export const TWIPS_PER_PT = 20;
export const TWIPS_PER_INCH = 1440;
export const TWIPS_PER_MM = 1440 / 25.4;
export const TWIPS_PER_CM = TWIPS_PER_MM * 10;
export const EMU_PER_MM = 36000;

export const mmToTwips = (mm: number): number => Math.round(mm * TWIPS_PER_MM);
export const twipsToMm = (tw: number): number => tw / TWIPS_PER_MM;
export const cmToTwips = (cm: number): number => Math.round(cm * TWIPS_PER_CM);
export const twipsToCm = (tw: number): number => tw / TWIPS_PER_CM;
export const ptToTwips = (pt: number): number => Math.round(pt * TWIPS_PER_PT);
export const twipsToPt = (tw: number): number => tw / TWIPS_PER_PT;
export const emuToMm = (emu: number): number => emu / EMU_PER_MM;

/**
 * Разбирает размер в twip. В Transitional OOXML это целое число,
 * в Strict допускаются единицы измерения ("2cm", "12pt").
 */
export function parseTwips(v: string | undefined): number | undefined {
  if (v == null || v === '') return undefined;
  const m = /^\s*(-?\d+(?:\.\d+)?)\s*(mm|cm|in|pt|pc|pi)?\s*$/.exec(v);
  if (!m) return undefined;
  const n = parseFloat(m[1]!);
  switch (m[2]) {
    case undefined:
      return Math.round(n);
    case 'mm':
      return Math.round(n * TWIPS_PER_MM);
    case 'cm':
      return Math.round(n * TWIPS_PER_CM);
    case 'in':
      return Math.round(n * TWIPS_PER_INCH);
    case 'pt':
      return Math.round(n * TWIPS_PER_PT);
    default:
      return Math.round(n * 240);
  }
}

export function parseIntAttr(v: string | undefined): number | undefined {
  if (v == null || v === '') return undefined;
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : undefined;
}

/** Число в русской записи: 1.25 → «1,25», 30 → «30». */
export function fmtNum(n: number, digits = 2): string {
  const rounded = Number(n.toFixed(digits));
  return String(rounded).replace('.', ',');
}

export const fmtMm = (tw: number): string => `${fmtNum(twipsToMm(tw), 1)} мм`;
export const fmtCm = (tw: number): string => `${fmtNum(twipsToCm(tw), 2)} см`;
export const fmtPt = (pt: number): string => `${fmtNum(pt, 1)} пт`;
